import type { TUser } from '@/types/User.types';

type Invitation = Pick<
  TUser,
  'invitation_status' | 'invitation_link' | 'invitation_actions' | 'blocked_actions' | 'revoke_state' | 'resolution'
>;

export type TInvitationLabel =
  | 'Needs support'
  | 'Sanitizing'
  | 'Reissuing'
  | 'Removing'
  | 'Unfinished revoke'
  | 'Accepted'
  | 'Expired'
  | 'Pending';

// Shown on hover of the status badge: what the status means and what to do next.
export const invitationLabelDescriptions: Record<TInvitationLabel, string> = {
  Accepted: 'The user accepted the invitation and set up their account.',
  Pending: 'Invitation sent. Waiting for the user to accept it.',
  Expired:
    'The invitation link expired or can no longer be used. Send a new one with Reinvite in the actions menu.',
  'Unfinished revoke': 'Revoking this invitation didn’t finish. Use Retry revoke in the actions menu.',
  'Needs support': 'This account needs support to resolve.',
  Sanitizing: 'The account’s data is being sanitized. This usually finishes shortly.',
  Reissuing: 'A new invitation is being issued. This usually finishes shortly.',
  Removing: 'The account is being removed. This usually finishes shortly.',
};

export const getInvitationLabel = (invitation: Invitation): TInvitationLabel => {
  if (invitation.resolution?.state === 'BLOCKED') return 'Needs support';
  if (invitation.resolution?.state === 'SANITIZING') return 'Sanitizing';
  if (invitation.resolution?.state === 'REISSUING') return 'Reissuing';
  if (invitation.resolution?.state === 'REMOVING') return 'Removing';
  if (invitation.resolution?.state !== 'DONE') {
    if (invitation.revoke_state === 'NEEDS_REPAIR') return 'Needs support';
    if (invitation.revoke_state === 'UNFINISHED') return 'Unfinished revoke';
  }
  if (invitation.invitation_status === 'ACTIVE') return 'Accepted';
  if (invitation.invitation_link === 'EXPIRED' || invitation.invitation_link === 'UNUSABLE') return 'Expired';
  return 'Pending';
};

export const canReinvite = (invitation: Invitation): boolean =>
  invitation.invitation_actions?.includes('REINVITE') === true;

export const canRevoke = (invitation: Invitation): boolean =>
  invitation.invitation_actions?.includes('REVOKE') === true;

const blockedReasonLabels: Record<NonNullable<TUser['blocked_actions']>[string], string> = {
  SETUP_IN_PROGRESS: 'Setup is in progress',
  NEEDS_REPAIR: 'This invitation needs repair',
  REVOKE_UNFINISHED: 'Revocation is unfinished',
  REVOKE_IN_PROGRESS: 'Revocation is in progress',
  READ_ONLY: 'You have read only access',
  NOT_AUTHORIZED: 'You are not authorized to reinvite',
  NOT_SUPPORTED: 'Reinvite is not supported',
  ALREADY_ACCEPTED: 'This invitation was already accepted',
  RESOLUTION_CONFLICT: 'A different resolution was already recorded',
  TENANT_NOT_READY: 'Account recovery is not ready for this organization',
};

export const getReinviteBlockedReason = (invitation: Invitation): string | undefined => {
  const reason = invitation.blocked_actions?.REINVITE;
  return reason ? (blockedReasonLabels[reason] ?? reason.replaceAll('_', ' ').toLowerCase()) : undefined;
};

export const getRevokeBlockedReason = (invitation: Invitation): string | undefined => {
  const reason = invitation.blocked_actions?.REVOKE;
  return reason ? revokeBlockedReasonLabels[reason] : undefined;
};

const revokeBlockedReasonLabels: Record<NonNullable<TUser['blocked_actions']>[string], string> = {
  SETUP_IN_PROGRESS: 'Account setup is in progress. Try again shortly.',
  NEEDS_REPAIR: 'This invitation needs support to resolve it.',
  REVOKE_UNFINISHED: 'The previous revoke is unfinished. Retry revoke.',
  REVOKE_IN_PROGRESS: 'Another revoke for this email is running.',
  READ_ONLY: 'This roster is in read-only mode.',
  NOT_AUTHORIZED: 'This action is not available to you.',
  NOT_SUPPORTED: 'This action is not available for this invitation.',
  ALREADY_ACCEPTED: 'This invitation was already accepted.',
  RESOLUTION_CONFLICT: 'A different decision was already recorded.',
  TENANT_NOT_READY: 'Account recovery is not ready for this organization.',
};

export const resolutionBlockedReasonLabels: Record<
  NonNullable<NonNullable<TUser['resolution']>['blocked_reason']>,
  string
> = {
  ORGANIZATION_INACTIVE: 'The organization is inactive. Support must reactivate it before continuing.',
  SANITIZE_FAILED: 'Sanitization failed. Support must review this account.',
  TENANT_NOT_READY: 'The organization is not ready for this resolution. Support must review it.',
  ACCOUNT_CHANGED: 'The account changed during resolution. Support must review it.',
};
